import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const POINTS = [
  "Designed around your workflows, customers and teams",
  "Built with modern, maintainable technology",
  "Security and reliability considered from day one",
  "Architecture that grows with your business",
];

export default function FeaturedCapability() {
  return (
    <section className="relative overflow-hidden border-y border-slate-800/60 bg-navy py-28 sm:py-36" data-testid="featured-capability">
      <div className="bg-grid absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_at_70%_50%,black_20%,transparent_70%)]" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
        <Reveal>
          <Eyebrow>Featured Capability</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">
            Custom Software Development
          </h2>
          <p className="mt-5 font-display text-lg text-slate-200 sm:text-xl">
            Software designed around the way your business actually works.
          </p>
          <p className="mt-4 text-base leading-relaxed text-slate-400">
            Off-the-shelf systems force your business into rigid shapes. We take the
            opposite approach: we design software around your workflows, your
            customers, your teams, and your growth plans — so the technology fits
            the business, not the other way around.
          </p>
          <ul className="mt-8 space-y-3">
            {POINTS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-slate-300">
                <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-blue-400" />
                {p}
              </li>
            ))}
          </ul>
          <Link
            to="/services/software-development"
            data-testid="featured-explore-btn"
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_0_24px_rgba(59,130,246,0.35)] transition-[background-color,box-shadow,transform] duration-300 hover:bg-blue-500 active:scale-[0.98]"
          >
            Explore Software Development
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative mx-auto w-full max-w-md">
            <div className="absolute -inset-6 rounded-[2rem] bg-blue-500/10 blur-2xl" aria-hidden="true" />
            <div className="glass relative rounded-2xl p-6" data-testid="featured-architecture-visual">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-blue-400">System Architecture</p>
              <div className="mt-5 space-y-3">
                {[
                  { label: "Experience Layer", desc: "Web · Mobile-ready UI", w: "100%" },
                  { label: "Application Core", desc: "Your business logic", w: "82%" },
                  { label: "Integrations", desc: "APIs · Webhooks · Data", w: "64%" },
                  { label: "Data Foundation", desc: "Secure · Scalable", w: "46%" },
                ].map((layer, i) => (
                  <div
                    key={layer.label}
                    className="rounded-xl border border-slate-700/70 bg-slate-900/70 px-4 py-3 transition-colors duration-300 hover:border-blue-500/50"
                    style={{ marginLeft: `${i * 8}%` }}
                  >
                    <p className="font-display text-sm font-semibold text-slate-100">{layer.label}</p>
                    <p className="font-mono text-[10px] text-slate-500">{layer.desc}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center gap-2 border-t border-slate-800 pt-4">
                <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-emerald-400" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  Every layer designed for your operation
                </span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
